import { UniqueHtmlElement } from './unique-html-element';

export type StrokeLinecap = 'butt' | 'round' | 'square';

export class UniqueSvgElement extends UniqueHtmlElement<HTMLElement> {
  protected svgElement!: SVGElement;

  constructor(selector: string, protected scaleFactor = 2) {
    super(selector);

    if (this.element instanceof SVGElement) {
      this.svgElement = this.element;
    } else {
      throw new Error('element was not an svg element');
    }
  }

  protected createGroup() {
    return document.createElementNS('http://www.w3.org/2000/svg', 'g');
  }

  protected createRect() {
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');

    return rect;
  }

  protected createLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    strokeColor: string,
    strokeLinecap: StrokeLinecap = 'butt',
    strokeWidth = 2,
  ) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');

    this.setStrokeWidth(strokeWidth, line);
    this.setStroke(strokeColor, line);

    this.setX1(x1, line);
    this.setY1(y1, line);
    this.setX2(x2, line);
    this.setY2(y2, line);
    this.setStrokeLinecap(strokeLinecap);

    return line;
  }

  protected createLineSquare(
    x: number,
    y: number,
    width: number,
    height: number,
    strokeColor: string,
  ): SVGElement {
    const group = this.createGroup();

    const leftEdge = this.createLine(
      x,
      y,
      x,
      y + height,
      strokeColor,
      'square',
      1,
    );
    const topEdge = this.createLine(
      x,
      y,
      x + width,
      y,
      strokeColor,
      'square',
      1,
    );
    const rightEdge = this.createLine(
      x + width,
      y,
      x + width,
      y + height,
      strokeColor,
      'square',
      1,
    );
    const bottomEdge = this.createLine(
      x,
      y + height,
      x + width,
      y + height,
      strokeColor,
      'square',
      1,
    );

    group.append(leftEdge, topEdge, rightEdge, bottomEdge);

    const rect = this.createRect();

    return group;
  }

  protected setHeight(height: number, element?: SVGElement | null) {
    this.set('height', `${this.round(height)}px`, element);
  }

  protected setWidth(width: number, element?: SVGElement | null) {
    this.set('width', `${this.round(width)}px`, element);
  }

  protected setViewBox(viewBox: string, element?: SVGElement | null) {
    this.set('viewBox', viewBox, element);
  }

  protected setX(x: number, element?: SVGElement | null) {
    this.set('x', `${this.round(x)}px`, element);
  }

  protected setY(y: number, element?: SVGElement | null) {
    this.set('y', `${this.round(y)}px`, element);
  }

  protected setX1(x: number, element?: SVGElement | null) {
    this.set('x1', `${this.round(x)}px`, element);
  }

  protected setY1(y: number, element?: SVGElement | null) {
    this.set('y1', `${this.round(y)}px`, element);
  }

  protected setX2(x: number, element?: SVGElement | null) {
    this.set('x2', `${this.round(x)}px`, element);
  }

  protected setY2(y: number, element?: SVGElement | null) {
    this.set('y2', `${this.round(y)}px`, element);
  }

  protected setFill(fill: string, element?: SVGElement | null) {
    this.set('fill', fill, element);
  }

  protected setStroke(color: string, element?: SVGElement | null) {
    this.set('stroke', color, element);
  }

  protected setStrokeWidth(width: number, element?: SVGElement | null) {
    this.set('stroke-width', `${this.round(width)}px`, element);
  }

  protected setStrokeLinecap(
    linecap: 'butt' | 'round' | 'square',
    element?: SVGElement | null,
  ) {
    this.set('stroke-linecap', linecap, element);
  }

  protected set(
    key:
      | 'fill'
      | 'y'
      | 'x'
      | 'y1'
      | 'x1'
      | 'y2'
      | 'x2'
      | 'viewBox'
      | 'width'
      | 'height'
      | 'stroke'
      | 'stroke-width'
      | 'stroke-linecap',
    value: string,
    element?: SVGElement | null,
  ) {
    (element ?? this.svgElement).setAttributeNS(null, key, value);
  }

  protected getChild<E extends Element = Element>(
    selectors: string,
    element?: SVGElement | null,
  ): E | null {
    return (element ?? this.svgElement).querySelector<E>(selectors);
  }

  protected getChildren<E extends Element = Element>(
    selectors: string,
    element?: SVGElement | null,
  ): NodeListOf<E> {
    return (element ?? this.svgElement).querySelectorAll<E>(selectors);
  }

  protected appendChild<T extends Node>(node: T): T {
    return this.svgElement.appendChild(node);
  }

  private round(n: number) {
    return n;
  }
}
